import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ListAgreement from '../../models/general/agreements/list-agreement';
import { useTranslation } from 'react-i18next';
import { DeleteIcon, PreviewIcon, UploadIcon } from '@common/utils/icons/icons';
import CustomLabelTextTable from '../misc/CustomLabelTextTable';
import { ContentType } from '../../models/enums/agreements';
import TooltipIconButton from '@common/components/Inputs/TooltipIconButton';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectAgreementsData } from '../../store/general/general-slice-selectors';
import UploadFileForm from './UploadFileForm';
import CustomConfirmAbortDialog from '../misc/CustomConfirmAbortDialog';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { deleteAgreementFileById } from '../../store/general/general-actions';
import CustomSnackbar from '@common/components/Feedback/CustomSnackbar';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { resetAgreementsRestState } from '../../store/general/general-slice';
import DocumentPreviewDialog from './DocumentPreviewDialog';

const AgreementContent: React.FC<{ agreement: ListAgreement }> = ({ agreement }) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const agreementsData = useAppSelector((state) => selectAgreementsData(state));

  const [isUploadFileFormOpen, setIsUploadFileFormOpen] = useState(false);
  const [deleteFileId, setDeleteFileId] = useState<string | null>(null);
  const [previewDocumentUrl, setPreviewDocumentUrl] = useState<string | null>(null);

  const openUploadFileFormClickHandler = () => {
    setIsUploadFileFormOpen(true);
  };

  const closeUploadFileFormHandler = () => {
    setIsUploadFileFormOpen(false);
  };

  const openConfirmDeleteDialogClickHandler = (id: string) => {
    setDeleteFileId(id);
  };

  const closeConfirmDeleteDialogHandler = () => {
    setDeleteFileId(null);
  };

  const openPreviewDocumentClickHandler = (url: string) => {
    setPreviewDocumentUrl(url);
  };

  const closePreviewDocumentHandler = () => {
    setPreviewDocumentUrl(null);
  };

  const deleteFileHandler = () => {
    if (deleteFileId != null) {
      dispatch(deleteAgreementFileById({ agreementId: agreement.id, fileId: deleteFileId }));
      setDeleteFileId(null);
    }
  };

  const closeDeleteFileSnackbarHandler = () => {
    dispatch(
      resetAgreementsRestState({
        deleteUploadFileError: null,
        deleteUploadFileStatus: HttpFetchStatus.Idle,
      }),
    );
  };

  const previousAgreement = (agreementsData.data ?? []).find(
    (a) => a.id === agreement.previousAgreement,
  );

  return (
    <React.Fragment>
      <Stack>
        <Stack direction="row" alignContent="center" justifyContent="space-between" pt={4}>
          <Typography variant="h2">{t('title-content')}</Typography>
          {agreement.contentType === ContentType.FILE && (
            <Button startIcon={<UploadIcon />} onClick={openUploadFileFormClickHandler}>
              {t('button-upload-file')}
            </Button>
          )}
        </Stack>
        <CustomLabelTextTable
          rows={[
            {
              label: t('table-header-agreement-content-type'),
              text: t('agreement-content-type-' + agreement.contentType) as React.ReactNode,
            },
            ...(previousAgreement != null
              ? [
                  {
                    label: t('table-header-agreement-previous-agreement'),
                    text: previousAgreement.name,
                  },
                ]
              : []),
          ].concat(
            agreement.contentType === ContentType.LINK ||
              agreement.contentType === ContentType.SURVEY
              ? [
                  {
                    label: t('agreement-content-type-' + agreement.contentType),
                    text: agreement.link ?? '',
                  },
                ]
              : agreement.files.map((file) => ({
                  label: t('language-' + file.language),
                  text: (
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Typography>{file.file}</Typography>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <TooltipIconButton
                          tooltip={t('info-tooltip-preview-file')}
                          onClick={() => openPreviewDocumentClickHandler(file.file)}
                          icon={<PreviewIcon data-testid="preview-icon" />}
                          color="primary"
                        />
                        <TooltipIconButton
                          tooltip={t('info-tooltip-delete-file')}
                          onClick={() => openConfirmDeleteDialogClickHandler(file.id)}
                          icon={<DeleteIcon data-testid="delete-icon" />}
                          color="error"
                        />
                      </Stack>
                    </Stack>
                  ),
                })),
          )}
        />
      </Stack>

      <UploadFileForm
        open={isUploadFileFormOpen}
        onClose={closeUploadFileFormHandler}
        agreementId={agreement.id}
      />
      <DocumentPreviewDialog url={previewDocumentUrl} onClose={closePreviewDocumentHandler} />
      <CustomConfirmAbortDialog
        open={deleteFileId != null}
        title={t('title-delete-agreement-file')}
        text={<CustomAlert severity="warning" header={t('warning-deleting-is-permanent')} />}
        confirmButton={t('button-delete')}
        abortButton={t('button-cancel')}
        onAbort={closeConfirmDeleteDialogHandler}
        onConfirm={deleteFileHandler}
      />
      <CustomSnackbar
        open={agreementsData.rest.deleteUploadFileStatus === HttpFetchStatus.Success}
        title={t('success-deleting-file')}
        onClose={closeDeleteFileSnackbarHandler}
      />
      <CustomSnackbar
        open={agreementsData.rest.deleteUploadFileStatus === HttpFetchStatus.Failed}
        title={t('error-deleting-file')}
        severity="error"
        onClose={closeDeleteFileSnackbarHandler}
      />
    </React.Fragment>
  );
};

export default AgreementContent;
